import React from 'react';
import { FiCircle } from 'react-icons/fi';
import {
  Mafs,
  Coordinates,
  Plot,
  Point,
  Text,
  Vector,
  Circle,
  Polygon,
  Line,
} from 'mafs';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

// Visual Components
const CircleBasicsVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'left',
        marginBottom: '1rem',
        color: 'black',
        paddingLeft: '1rem',
        fontSize: '1.5rem',
        fontWeight: 'normal',
      }}
    >
      Parts of a Circle
    </h4>
    <Mafs
      width={500}
      height={400}
      viewBox={{ x: [-5, 5], y: [-4.5, 4.5] }}
      pan={false}
    >
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      <Circle
        center={[0, 0]}
        radius={3}
        stroke="black"
        strokeWidth={1.5}
        fillColor="#e0f2fe"
      />

      <Polygon
        points={[
          [-2, 2.236],
          ...Array.from({ length: 21 }).map((_, i) => {
            const angle =
              Math.acos(2 / 3) -
              (i / 20) * (Math.acos(-2 / 3) - Math.acos(2 / 3));
            return [3 * Math.cos(angle), 3 * Math.sin(angle)];
          }),
          [2, 2.236],
        ]}
        color="#83c5e9"
      />
      <Text x={3} y={1} size={18} color="#0891b2" weight="bold">
        Segment
      </Text>

      <Polygon
        points={[
          [0, 0],
          ...Array.from({ length: 21 }).map((_, i) => {
            const angle = -Math.PI / 9 - (i / 20) * (Math.PI / 3);
            return [3 * Math.cos(angle), 3 * Math.sin(angle)];
          }),
        ]}
        color="#f8d775"
      />
      <Text x={2.3} y={-2.7} size={18} color="#d97706" weight="bold">
        Sector
      </Text>

      <Line.Segment point1={[-3, 0]} point2={[3, 0]} color="black" />
      <Text x={-1.3} y={0.3} size={18} color="#1f2937" weight="bold">
        Diameter
      </Text>

      <Point x={0} y={0} color="black" />
      <Text x={-1.2} y={-0.4} size={18} color="#1f2937" weight="bold">
        Center
      </Text>
      <Vector
        tail={[-0.8, -0.6]}
        tip={[-0.1, -0.1]}
        color="black"
        strokeWidth={1}
      />

      <Line.Segment
        point1={[0, 0]}
        point2={[3 * Math.cos(-Math.PI / 4.5), 3 * Math.sin(-Math.PI / 4.5)]}
        color="red"
      />
      <Text x={1.7} y={-1} size={18} color="#dc2626" weight="bold">
        Radius
      </Text>

      <Line.Segment point1={[-2, 2.236]} point2={[2, 2.236]} color="green" />
      <Text x={-0.4} y={1.8} size={18} color="#059669" weight="bold">
        Chord
      </Text>

      <Plot.OfX y={(x) => 0.15 * x + 3.02} domain={[-4.5, 3.5]} color="black" />
      <Text x={-2.5} y={3} size={18} color="#1f2937" weight="bold">
        Tangent
      </Text>

      <Plot.OfX y={(x) => 0.2 * x - 2} domain={[-4, 4.8]} color="blue" />
      <Text x={-3} y={-1.9} size={18} color="#1d4ed8" weight="bold">
        Secant
      </Text>

      <Plot.Parametric
        t={[-Math.PI / 10, Math.PI / 10]}
        xy={(t) => [3.15 * Math.cos(t), 3.15 * Math.sin(t)]}
        color="black"
        strokeDasharray="2 2"
      />
      <Text x={3.5} y={0} size={18} color="#1f2937" weight="bold">
        Arc
      </Text>
    </Mafs>
  </div>
);

const CircleEquationsVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Circle Equations
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-5, 5], y: [-3, 5] }}>
      <Coordinates.Cartesian />

      <Circle
        center={[0, 0]}
        radius={2}
        color="#2563eb"
        strokeWidth={2}
        fillOpacity={0.1}
      />
      <Text x={0} y={-3} color="#1d4ed8" size={14} weight="bold">
        x² + y² = r²
      </Text>
      <Text x={0} y={-4} color="#1d4ed8" size={12}>
        r = 2
      </Text>

      <Circle
        center={[2, 2]}
        radius={1.5}
        color="#10b981"
        strokeWidth={2}
        fillOpacity={0.1}
      />
      <Text x={2} y={4.5} color="#059669" size={14} weight="bold">
        (x-h)² + (y-k)² = r²
      </Text>
      <Text x={2} y={4} color="#059669" size={12}>
        Center: (2,2), r = 1.5
      </Text>

      <Point x={0} y={0} color="#2563eb" />
      <Point x={2} y={2} color="#10b981" />
    </Mafs>
  </div>
);

const CircleAreaVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Circle Area & Circumference
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-4, 4], y: [-3, 3] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      <Circle
        center={[0, 0]}
        radius={2}
        color="#2563eb"
        strokeWidth={3}
        fillOpacity={0.3}
      />

      <Plot.OfX y={() => 0} color="#ef4444" strokeWidth={3} domain={[0, 2]} />
      <Text x={1} y={0.3} color="#dc2626" size={14} weight="bold">
        r = 2
      </Text>

      <Text x={0} y={-2.5} color="#1d4ed8" size={16} weight="bold">
        Area = πr² = π(2)² = 4π
      </Text>

      <Text x={0} y={2.5} color="#1d4ed8" size={16} weight="bold">
        Circumference = 2πr = 2π(2) = 4π
      </Text>

      <Point x={0} y={0} color="#ef4444" />
    </Mafs>
  </div>
);

const TangentVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Tangent Lines
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-4, 4], y: [-3, 3] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      <Circle
        center={[0, 0]}
        radius={2}
        color="#2563eb"
        strokeWidth={2}
        fillOpacity={0.1}
      />

      <Plot.OfY x={() => 2} color="#10b981" strokeWidth={2} domain={[-1, 3]} />

      <Point x={2} y={0} color="#ef4444" />

      <Plot.OfX y={() => 0} color="#f59e0b" strokeWidth={2} domain={[0, 2]} />

      <Polygon
        points={[
          [1.8, 0],
          [1.8, 0.2],
          [2, 0.2],
        ]}
        color="#ef4444"
        strokeWidth={1}
        fillOpacity={0}
      />

      <Text x={2.3} y={1.5} color="#059669" size={14} weight="bold">
        Tangent
      </Text>
      <Text x={1} y={-0.4} color="#d97706" size={14} weight="bold">
        Radius ⊥ Tangent
      </Text>
      <Text x={0} y={-2.5} color="#1f2937" size={14} weight="bold">
        Tangent line touches circle at exactly one point
      </Text>

      <Point x={0} y={0} color="#2563eb" />
    </Mafs>
  </div>
);

const CirclesLesson = () => {
  const sections = [
    {
      id: 'circle-basics',
      title: 'Circle Basics',
      content: {
        introduction:
          'A circle is the set of all points equidistant from a center point. Understanding circle components is essential for solving circle problems.',

        concepts: [
          {
            title: 'Circle Components',
            explanation:
              'Learn the key parts of a circle: center, radius, diameter, chord, arc, tangent, and secant. Each component has specific properties and relationships.',
            visual: <CircleBasicsVisual />,
            example: {
              problem:
                'In a circle with center O and radius 5, what is the length of the diameter?',
              solution: [
                'The diameter is twice the radius',
                'Diameter = 2 × radius = 2 × 5 = 10',
                'The diameter passes through the center',
                "It's the longest chord in the circle",
              ],
            },
          },
        ],
      },
    },
    {
      id: 'circle-equations',
      title: 'Circle Equations',
      content: {
        introduction:
          'Circle equations help us represent circles algebraically and solve coordinate geometry problems involving circles.',

        concepts: [
          {
            title: 'Standard and General Forms',
            explanation:
              'Standard form: §(x-h)^2 + (y-k)^2 = r^2§ where (h,k) is center and r is radius. General form: §x^2 + y^2 + Dx + Ey + F = 0§.',
            visual: <CircleEquationsVisual />,
            example: {
              problem:
                'Find the center and radius of the circle §x^2 + y^2 - 6x + 4y - 3 = 0§',
              solution: [
                'Complete the square for x and y terms',
                '§(x^2 - 6x) + (y^2 + 4y) = 3§',
                '§(x^2 - 6x + 9) + (y^2 + 4y + 4) = 3 + 9 + 4§',
                '§(x - 3)^2 + (y + 2)^2 = 16§',
                'Center: (3, -2), Radius: §\\sqrt{16} = 4§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'area-circumference',
      title: 'Area & Circumference',
      content: {
        introduction:
          'Area and circumference are fundamental measurements of circles. Both involve π (pi) and depend on the radius.',

        concepts: [
          {
            title: 'Circle Measurements',
            explanation:
              'Area = §\\pi r^2§ gives the space inside the circle. Circumference = §2\\pi r§ gives the distance around the circle. §\\pi \\approx 3.14159§.',
            visual: <CircleAreaVisual />,
            example: {
              problem:
                'A circular garden has circumference §18\\pi§ meters. Find its area.',
              solution: [
                'Use circumference to find radius: §2\\pi r = 18\\pi§',
                'Divide by §2\\pi§: §r = 18\\pi/(2\\pi) = 9§ meters',
                'Calculate area: §A = \\pi r^2 = \\pi(9)^2 = 81\\pi§',
                'Area = §81\\pi \\approx 254.5§ square meters',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'tangents',
      title: 'Tangents & Chords',
      content: {
        introduction:
          'Tangent lines and chords have special properties in circles. Understanding these relationships helps solve complex problems.',

        concepts: [
          {
            title: 'Tangent Properties',
            explanation:
              'A tangent touches the circle at exactly one point. The radius to the point of tangency is perpendicular to the tangent line.',
            visual: <TangentVisual />,
            example: {
              problem:
                'From external point P, two tangents are drawn to a circle. If the radius is 5 and distance from P to center is 13, find the tangent length.',
              solution: [
                'Use the tangent-secant theorem',
                '§\\text{Tangent}^2 = (\\text{distance to center})^2 - \\text{radius}^2§',
                '§\\text{Tangent}^2 = 13^2 - 5^2 = 169 - 25 = 144§',
                'Tangent length = §\\sqrt{144} = 12§',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question: 'A circle has radius 6. What is its area?',
      options: ['§36\\pi§', '§12\\pi§', '§6\\pi§', '§18\\pi§', '§24\\pi§'],
      correct: 0,
      explanation: 'Area = §\\pi r^2 = \\pi(6)^2 = 36\\pi§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'What is the circumference of a circle with diameter 14?',
      options: ['§14\\pi§', '§7\\pi§', '§28\\pi§', '§49\\pi§', '§21\\pi§'],
      correct: 0,
      explanation: 'Circumference = §\\pi d = \\pi(14) = 14\\pi§',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'A circle has equation §(x-3)^2 + (y+2)^2 = 25§. What is its center?',
      options: ['(3, -2)', '(-3, 2)', '(3, 2)', '(-3, -2)', '(5, 5)'],
      correct: 0,
      explanation:
        'From §(x-h)^2 + (y-k)^2 = r^2§, the center is (h,k) = §(3,-2)§',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A circle is inscribed in a square with side length 10. What is the radius of the circle?',
      options: ['5', '10', '5√2', '10√2', '2.5'],
      correct: 0,
      explanation:
        'The diameter of the inscribed circle equals the side of the square. So diameter = 10, radius = 5.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'A circular garden has a path 2 meters wide around it. If the garden radius is 10m, what is the area of the path?',
      options: ['24π m²', '44π m²', '48π m²', '84π m²', '40π m²'],
      correct: 1,
      explanation:
        'Outer radius = §10 + 2 = 12§m. Path area = §\\pi(12²) - \\pi(10²) = 144\\pi - 100\\pi = 44\\pi§ m².',
    },
    {
      type: 'timed',
      timeLimit: 95,
      question:
        'A circle with center (2, -3) passes through the point (6, 0). What is its radius?',
      options: ['5', '4', '3', '6', '7'],
      correct: 0,
      explanation:
        'Use distance formula: §r = \\sqrt{(6-2)² + (0-(-3))²} = \\sqrt{16 + 9} = \\sqrt{25} = 5§.',
    },
    {
      type: 'timed',
      timeLimit: 115,
      question:
        'An arc of a circle with radius 12 has a central angle of 60°. What is the length of the arc?',
      options: ['4π', '6π', '8π', '2π', '12π'],
      correct: 0,
      explanation:
        'Arc length = §(\\theta/360°) \\times 2\\pi r = (60°/360°) \\times 2\\pi(12) = (1/6) \\times 24\\pi = 4\\pi§.',
    },
    {
      type: 'timed',
      timeLimit: 125,
      question:
        'A sector of a circle with radius 9 has a central angle of 40°. What is its area?',
      options: ['9π', '18π', '27π', '36π', '81π'],
      correct: 0,
      explanation:
        'Sector area = §(\\theta/360°) \\times \\pi r² = (40°/360°) \\times \\pi(81) = (1/9) \\times 81\\pi = 9\\pi§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'What is the relationship between a radius and a tangent line at the point of tangency?',
      options: [
        'Perpendicular',
        'Parallel',
        'Equal length',
        'Bisecting',
        'Coincident',
      ],
      correct: 0,
      explanation:
        'The radius to the point of tangency is always perpendicular to the tangent line.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'If a circle has area 49π, what is its radius?',
      options: ['7', '14', '49', '24.5', '3.5'],
      correct: 0,
      explanation: 'Area = §\\pi r² = 49\\pi§, so §r² = 49§, therefore §r = 7§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'Two circles are concentric (same center). The larger has radius 10, the smaller has radius 6. What is the area between them?',
      options: ['64π', '36π', '16π', '100π', '4π'],
      correct: 0,
      explanation:
        'Area between = §\\pi(10²) - \\pi(6²) = 100\\pi - 36\\pi = 64\\pi§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'A chord of a circle is 16 units long and is 6 units from the center. What is the radius?',
      options: ['10', '8', '12', '14', '6'],
      correct: 0,
      explanation:
        'Half chord = 8. Forms right triangle: §r² = 6² + 8² = 36 + 64 = 100§, so §r = 10§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question: 'A circle has circumference 20π. What is its area?',
      options: ['100π', '400π', '40π', '10π', '200π'],
      correct: 0,
      explanation:
        'Circumference = §2\\pi r = 20\\pi§, so §r = 10§. Area = §\\pi r² = \\pi(100) = 100\\pi§.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'The equation x² + y² - 8x + 6y = 0 represents a circle. What is its radius?',
      options: ['5', '10', '25', '3', '4'],
      correct: 0,
      explanation:
        'Complete the square: §(x² - 8x + 16) + (y² + 6y + 9) = 16 + 9§ → §(x-4)² + (y+3)² = 25§. Radius = §\\sqrt{25} = 5§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question: 'A semicircle has diameter 12. What is its perimeter?',
      options: [
        '§6\\pi + 12§',
        '§12\\pi + 12§',
        '§6\\pi§',
        '§24\\pi§',
        '§18\\pi§',
      ],
      correct: 0,
      explanation:
        'Perimeter = half circumference + diameter = §(1/2)(\\pi d) + d = 6\\pi + 12§.',
    },
    {
      type: 'untimed',
      question:
        'What is the relationship between a radius and a tangent line at the point of tangency?',
      options: [
        'Perpendicular',
        'Parallel',
        'Equal length',
        'Bisecting',
        'Coincident',
      ],
      correct: 0,
      explanation:
        'The radius to the point of tangency is always perpendicular to the tangent line.',
    },
    {
      type: 'untimed',
      question: 'If a circle has area 49π, what is its radius?',
      options: ['7', '14', '49', '24.5', '3.5'],
      correct: 0,
      explanation: 'Area = §\\pi r² = 49\\pi§, so §r² = 49§, therefore §r = 7§',
    },
    {
      type: 'untimed',
      question:
        'Two circles are concentric (same center). The larger has radius 10, the smaller has radius 6. What is the area between them?',
      options: ['64π', '36π', '16π', '100π', '4π'],
      correct: 0,
      explanation:
        'Area between = §\\pi(10²) - \\pi(6²) = 100\\pi - 36\\pi = 64\\pi§.',
    },
    {
      type: 'untimed',
      question:
        'A chord of a circle is 16 units long and is 6 units from the center. What is the radius?',
      options: ['10', '8', '12', '14', '6'],
      correct: 0,
      explanation:
        'Half chord = 8. Forms right triangle: §r² = 6² + 8² = 36 + 64 = 100§, so §r = 10§.',
    },
    {
      type: 'untimed',
      question: 'A circle has circumference 20π. What is its area?',
      options: ['100π', '400π', '40π', '10π', '200π'],
      correct: 0,
      explanation:
        'Circumference = §2\\pi r = 20\\pi§, so §r = 10§. Area = §\\pi r² = \\pi(100) = 100\\pi§.',
    },
    {
      type: 'untimed',
      question:
        'The equation x² + y² - 8x + 6y = 0 represents a circle. What is its radius?',
      options: ['5', '10', '25', '3', '4'],
      correct: 0,
      explanation:
        'Complete the square: §(x² - 8x + 16) + (y² + 6y + 9) = 16 + 9§ → §(x-4)² + (y+3)² = 25§. Radius = §\\sqrt{25} = 5§.',
    },
    {
      type: 'untimed',
      question: 'A semicircle has diameter 12. What is its perimeter?',
      options: [
        '§6\\pi + 12§',
        '§12\\pi + 12§',
        '§6\\pi§',
        '§24\\pi§',
        '§18\\pi§',
      ],
      correct: 0,
      explanation:
        'Perimeter = half circumference + diameter = §(1/2)(\\pi d) + d = 6\\pi + 12§.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'A circle has radius 5. Compare the two quantities.',
      quantityA: 'Diameter',
      quantityB: 'Circumference divided by §pi§',
      correct: 2,
      explanation:
        'Compare the two quantities directly. The two quantities are equal.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'Which statements are true for any circle? Select all that apply.',
      options: [
        'Diameter is twice the radius',
        'Circumference is §2*pi*r§',
        'Area is §2*pi*r§',
        'All radii are equal',
        'A chord must pass through the center',
      ],
      correct: [0, 1, 3],
      explanation:
        'Diameter is §2r§, circumference is §2*pi*r§, and all radii in the same circle are equal.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'A circle has radius 7. Enter its diameter.',
      correctAnswer: '14',
      acceptedAnswers: ['14'],
      explanation: 'Diameter is twice the radius: §2*7=14§.',
    },
  ];

  const quickReference = [
    'Circle formula: §A = \\pi r^2§, §C = 2\\pi r§',
    'Arc length: §L = (\\theta/360°) \\times 2\\pi r§',
    'Sector area: §A = (\\theta/360°) \\times \\pi r^2§',
    'Inscribed angle = §\\frac{1}{2}§ central angle',
    'Tangent perpendicular to radius',
  ];

  const getStartedWithProps = {
    title: 'Need help with Circle concepts?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Circle concepts for the GRE Quantitative section',
    aicontext:
      'Master circle properties, equations, area, circumference, and tangent relationships',
  };

  return (
    <LessonTemplate
      lessonId="circles"
      lessonTitle="Circles"
      lessonIcon={<FiCircle />}
      lessonDescription="Master circle properties, equations, area, circumference, and tangent relationships"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={<QuantLessonPracticeSlot lessonId="circles" />}
    />
  );
};

export default CirclesLesson;
